import { CONFIG_SECTION } from '../components/add-employee/steps/Employee';
import { deepClone } from '../utility/Utility';
import CustomSectionManager from '../managers/CustomSectionManager';
import { TENANT, TenantService } from '../managers/TenantManagerService';

class EmployeeConfigService {
    static EMPLOYEE_CONFIG;
    static FIELD_PERMISSIONS;
    static EXCLUDE_SECTION = ['TERMINATION_DETAILS'];
    static EMPLOYEE_CONFIG_BY_SECTION = {};

    static setEmployeeConfig(config) {
        try {
            if (Array.isArray(config)) {
                this.EMPLOYEE_CONFIG = deepClone(config).find((c) => c.configName === 'EMPLOYEE_CONFIG');
                this.setEmployeeConfigBySection(this.EMPLOYEE_CONFIG);
            }
        } catch (err) {
            console.log(err);
        }
    }

    static setEmployeeConfigBySection(EMPLOYEE_CONFIG) {
        if (EMPLOYEE_CONFIG && Array.isArray(EMPLOYEE_CONFIG.sections)) {
            const employeeConfig = deepClone(EMPLOYEE_CONFIG);
            employeeConfig.sections.forEach((section) => {
                if (Array.isArray(section.fields)) {
                    const sortedFields = this.sortFieldsByIndex(section.fields);
                    this.EMPLOYEE_CONFIG_BY_SECTION[section.name] = sortedFields.map((field) => {
                        return {
                            id: field.id,
                            systemField: field.systemField
                        };
                    });
                }
            });
        }
    }

    static sortSectionsByPosition(sections) {
        let sortedSections = sections;
        if (Array.isArray(sections)) {
            sortedSections = sections
                .sort((s1, s2) => s1.position - s2.position)
                .map((section) => {
                    return {
                        ...section,
                        fields: this.sortFieldsByIndex(section.fields),
                    };
                });
        }

        return sortedSections;
    }

    static sortFieldsByIndex(fields) {
        let sortedFields = fields;
        if (Array.isArray(fields)) {
            sortedFields = fields.sort((f1, f2) => f1.index - f2.index);
        }

        return sortedFields;
    }

    static getSectionByName(sectionName) {
        let section;
        if (this.EMPLOYEE_CONFIG && Array.isArray(this.EMPLOYEE_CONFIG.sections)) {
            const employeeConfig = deepClone(this.EMPLOYEE_CONFIG);
            section = employeeConfig.sections.find((s) => s.name === sectionName);
        }

        return section;
    }

    static getDefaultFieldsBySection(sectionName) {
        let fields = [];
        const section = this.getSectionByName(sectionName);

        if (Array.isArray(section?.fields)) {
            fields = section.fields.filter((f) => {
                return f.systemField;
            });

            switch (sectionName) {
                case CONFIG_SECTION.EMPLOYEE_PAYMENT_DETAILS:
                    if (TenantService.get(TENANT.CUSTOM_FIELD_SECTION_ENABLED)) {
                        fields = fields.map((f) => {
                            return { ...f, required: false };
                        });
                    }
                    break;
                case CONFIG_SECTION.BILLING_RATE:
                    if (TenantService.get(TENANT.CUSTOM_FIELD_SECTION_ENABLED) && !TenantService.get(TENANT.IS_ADMIN)) {
                        fields = fields.map((f) => {
                            return { ...f, readOnly: true };
                        });
                    }
                    break;
                default:
                    break;
            }
        }

        return this.sortFieldsByIndex(fields);
    }

    static getNonDefaultFieldSections(pageStepNumber) {
        const sections = [];
        if (this.EMPLOYEE_CONFIG && Array.isArray(this.EMPLOYEE_CONFIG.sections)) {
            const employeeConfig = deepClone(this.EMPLOYEE_CONFIG);
            employeeConfig.sections.forEach((section) => {
                if (!this.EXCLUDE_SECTION.includes(section.name) && section.pageStepNumber === pageStepNumber) {
                    section.isExpanded = true;
                    if (!section.defaultSection) {
                        sections.push(section);
                    } else if (Array.isArray(section.fields)) {
                        const sectionToUpdate = { ...section };
                        sectionToUpdate.fields = section.fields.filter((f) => {
                            return !f.systemField;
                        });
                        sections.push(sectionToUpdate);
                    }
                }
            });
        }

        return this.sortSectionsByPosition(sections);
    }

    static getNonDefaultSectionsByName(customSections, sectionName, hasPermission, toRole) {
        let section;
        if (Array.isArray(customSections)) {
            section = customSections.find((section) => {
                return section.name === sectionName;
            });

            const fieldPermissions = this.getFieldPermissionsByRole(toRole);
            if (section && Array.isArray(section.fields)) {
                section.fields.forEach((field) => {
                    field.readOnly = !hasPermission;

                    if (fieldPermissions?.hasOwnProperty(field.id)) {
                        this.applyFieldPermissions(field, fieldPermissions[field.id]);
                    }
                });
            }

            section = this.removeHiddenFields(section);
        }

        return section;
    }

    static getNonDefaultSections(customSections, hasPermission, toRole) {
        let sections = [];
        if (Array.isArray(customSections)) {
            sections = customSections.filter((section) => {
                return !section.defaultSection;
            });

            const fieldPermissions = this.getFieldPermissionsByRole(toRole);
            sections.forEach((section) => {
                if (section && Array.isArray(section.fields)) {
                    section.fields.forEach((field) => {
                        field.readOnly = !hasPermission;

                        if (fieldPermissions?.hasOwnProperty(field.id)) {
                            this.applyFieldPermissions(field, fieldPermissions[field.id]);
                        }
                    });

                    section = this.removeHiddenFields(section);
                }
            });

            sections = this.removeEmptySection(sections);
        }

        return sections;
    }

    static removeHiddenFields(section) {
        if (section && Array.isArray(section.fields)) {
            section.fields = section.fields.filter((field) => {
                return !field.hidden;
            });
        }

        return section;
    }

    static removeEmptySection(sections) {
        return sections.filter((section) => {
            return section.fields?.length > 0;
        });
    }

    static getTerminationDetailSection(terminationDetails) {
        let section;
        if (this.EMPLOYEE_CONFIG && Array.isArray(this.EMPLOYEE_CONFIG.sections)) {
            const employeeConfig = deepClone(this.EMPLOYEE_CONFIG);
            section = employeeConfig.sections.find((section) => section.name === 'TERMINATION_DETAILS');
        }

        if (section && Array.isArray(section.fields)) {
            section.fields = this.sortFieldsByIndex(section.fields);
            section = CustomSectionManager.enrichWithTerminationDetailValues(section, terminationDetails);
            section.isExpanded = true;
        }

        return section;
    }

    static setFieldPermissions(fieldPermissions) {
        try {
            if (Array.isArray(fieldPermissions)) {
                this.FIELD_PERMISSIONS = fieldPermissions;
            }
        } catch (err) {
            console.log(err);
        }
    }

    static getFieldPermissionsByRole(toRole) {
        let permissions;
        if (Array.isArray(this.FIELD_PERMISSIONS)) {
            permissions = this.FIELD_PERMISSIONS.find((perm) => {
                return perm.toRole === toRole;
            });
        }

        return permissions?.fieldPermission || [];
    }

    static applyFieldPermissions(field, permission) {
        switch (permission) {
            case 'r':
                field.readOnly = true;
                break;
            case 'h':
                field.hidden = true;
                field.uiVisible = false;
                break;
            default:
                break;
        }
    }

    static refreshDropdownOptionsInEmployeeConfig(field) {
        if (this.EMPLOYEE_CONFIG && Array.isArray(this.EMPLOYEE_CONFIG.sections)) {
            this.EMPLOYEE_CONFIG.sections.forEach((section) => {
                if (Array.isArray(section.fields)) {
                    section.fields.forEach((f) => {
                        if (field.id === f.id) {
                            f.defaultOptions = field.options;
                            f.options = field.options;
                        }
                    });
                }
            });
        }
    }
}

export default EmployeeConfigService;
